/**
 * Which theme is active is resolved in CSS (tokens/roles/notifications.scss), never here: a toast
 * keeps the classes it was created with, so branching on `hasTheme` at creation time left a toast
 * opened before a theme was active stuck in the legacy styling for the rest of its life (HR-4740).
 *
 * @todo Delete the legacy .notify-* classes from src/css/app.scss once
 * components/ChangeRequest/ChangeRequestBanner.vue stops borrowing .notify-warning for its header —
 * no notification emits those classes any more.
 */

/** @typedef {import('quasar').QNotifyCreateOptions} QNotifyCreateOptions */
/** @typedef {(props?: import('quasar').QNotifyUpdateOptions) => void} NotifyDismiss */

import { Notify } from 'quasar';

const notification = (message, options = {}) => {
  if (!message) {
    return;
  }

  return Notify.create({ message, ...options });
};

// Caller classes must extend the base class rather than replace it — a caller-supplied
// class (e.g. the update banners' `notify-update-version`) would otherwise strip the
// background and message color, leaving Quasar's bare grey toast.
const withBaseClass = (baseClass, extraClasses) => (
  extraClasses ? `${baseClass} ${extraClasses}` : baseClass
);

// Action colors belong to the design system, not the call site: Quasar's color classes
// are `!important`, so a caller-supplied `color` beats the token colors in
// roles/notifications.scss and the label stops following the theme.
const withoutActionColors = (actions) => actions.map(
  (action) => ({ ...action, color: undefined, textColor: undefined }),
);

/**
 * Show a success notification toast.
 *
 * @param {string} message
 * @param {QNotifyCreateOptions} [opts]
 * @returns {NotifyDismiss | undefined}
 */
const notifySuccess = (message, opts = {}) => {
  const {
    attrs: extraAttrs = {}, classes: extraClasses, actions: customActions, ...rest
  } = opts;

  const actions = customActions ? withoutActionColors(customActions) : undefined;

  return notification(message, {
    classes: withBaseClass('hr-notify-success', extraClasses),
    icon: 'check',
    progress: true,
    progressClass: 'hr-notify-success__progress',
    timeout: 4000,
    ...(actions ? { actions } : {}),
    attrs: {
      'aria-live': 'polite',
      'data-e2e': 'snackbar-success',
      ...extraAttrs,
    },
    ...rest,
  });
};

/**
 * Show an informational notification toast.
 *
 * @param {string} message
 * @param {QNotifyCreateOptions} [opts]
 * @returns {NotifyDismiss | undefined}
 */
const notifyInfo = (message, opts = {}) => {
  const {
    attrs: extraAttrs = {}, classes: extraClasses, actions: customActions, ...rest
  } = opts;

  const actions = customActions ? withoutActionColors(customActions) : undefined;

  return notification(message, {
    classes: withBaseClass('hr-notify-info', extraClasses),
    icon: 'info',
    progress: true,
    progressClass: 'hr-notify-info__progress',
    timeout: 4000,
    ...(actions ? { actions } : {}),
    attrs: {
      'aria-live': 'polite',
      'data-e2e': 'snackbar-info',
      ...extraAttrs,
    },
    ...rest,
  });
};

/**
 * Show an error notification toast. Stays visible until manually dismissed.
 *
 * @param {string} message
 * @param {QNotifyCreateOptions} [opts]
 * @returns {NotifyDismiss | undefined}
 */
const notifyNegative = (message, opts = {}) => {
  const {
    attrs: extraAttrs = {}, classes: extraClasses, actions: customActions, ...rest
  } = opts;

  const actions = withoutActionColors(customActions ?? [
    { icon: 'close', size: 'sm', style: 'padding: 0 8px 0 8px' },
  ]);

  return notification(message, {
    timeout: 0,
    actions,
    classes: withBaseClass('hr-notify-error', extraClasses),
    icon: 'report_problem',
    attrs: {
      'aria-live': 'assertive',
      'data-e2e': 'snackbar-error',
      ...extraAttrs,
    },
    ...rest,
  });
};

/**
 * Show a warning notification toast. Stays visible until manually dismissed.
 *
 * @param {string} message
 * @param {QNotifyCreateOptions} [opts]
 * @returns {NotifyDismiss | undefined}
 */
const notifyWarning = (message, opts = {}) => {
  const {
    attrs: extraAttrs = {}, classes: extraClasses, actions: customActions, ...rest
  } = opts;

  const actions = withoutActionColors(customActions ?? [
    { icon: 'close', size: 'sm', style: 'padding: 0 8px 0 8px' },
  ]);

  return notification(message, {
    timeout: 0,
    actions,
    classes: withBaseClass('hr-notify-warning', extraClasses),
    icon: 'report',
    attrs: {
      'aria-live': 'assertive',
      'data-e2e': 'snackbar-warning',
      ...extraAttrs,
    },
    ...rest,
  });
};

export { notifySuccess, notifyInfo, notifyNegative, notifyWarning };
